import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import throttle from 'lodash/throttle';
import BottomSticky from 'core/modules/BottomSticky/BottomSticky';
import ToggleButton from './components/ToggleButton';
import LinksTray from './components/LinksTray';
import SearchIcon from './components/SearchIcon';
import { triggerQuickLinkTrayIsViewed } from './actions';

import config from './configs';

import './QuickLinksTray.scss';

function QuickLinksTray() {
  const dispatch = useDispatch();
  const initialAutoHideDelay = 3500;

  const cachedScroll = useRef(null);
  const autoHideTimeout = useRef(null);

  const [isExpanded, setIsExpanded] = useState(true);

  const toggleTray = (shouldExpand = false) => {
    setIsExpanded(shouldExpand);
  };

  const scrollHandler = () => {
    // If scroll event is caused by lightbox overlay or mobile navigation toggling then we ignore it in this handler:
    // 1. on lightbox overlay or sidebar opening the 'html' tag has the
    // 'overflow: hidden' and page automatically scrolls to top
    // 2. after lightbox overlay or sidebar is closed then the page scrolls back to previous position
    const htmlStyle = getComputedStyle(document.documentElement);
    if (htmlStyle.overflow === 'hidden' || htmlStyle.overflowY === 'hidden' ||
      cachedScroll.current === window.scrollY) {
      return false;
    }

    cachedScroll.current = window.scrollY;

    // Clear the auto hide timeout is exist
    // On mobile if scrolled to the top of a page we could have a sequence
    // of the scroll events with a negative or zero window.scroll value.
    // Since for the first of these events the navigation is expanded and autoHideTimeout is defined to auto hide
    // it later we should not cancel the auto hide delay on any of scroll events for the scroll position 0 or negative
    if (window.scrollY > 0 && autoHideTimeout.current) {
      clearTimeout(autoHideTimeout.current);
      autoHideTimeout.current = null;
    }
    // if scrolled to the top of the page then expand navigation if collapsed, auto collapse in after some delay
    if (window.scrollY <= 0) {
      if (!isExpanded) {
        toggleTray(true);
        autoHideTimeout.current = setTimeout(toggleTray, initialAutoHideDelay, false);
      }
      // else if scrolled to the bottom of the page then expand navigation if collapsed
    } else if (Math.ceil(window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
      if (!isExpanded) {
        toggleTray(true);
      }
      // Otherwise collapse navigation on page scroll if expanded
    } else if (isExpanded) {
      toggleTray(false);
    }

    return true;
  };

  const updateTrayOnScroll = throttle(scrollHandler, 50);

  useEffect(() => {
    autoHideTimeout.current = setTimeout(toggleTray, initialAutoHideDelay, false);
    scrollHandler();
    dispatch(triggerQuickLinkTrayIsViewed());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('scroll', updateTrayOnScroll);

    return () => {
      document.removeEventListener('scroll', updateTrayOnScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  return (
    <BottomSticky className="mn_quickLinks">
      <div className={`mn_quickLinksTrayContainer ${!isExpanded ? 'mn_collapsed' : 'mn_expanded'}`}>
        <SearchIcon />
        <ToggleButton isExpanded={isExpanded} clickHandler={() => toggleTray(true)} config={config} />
        <LinksTray isExpanded={isExpanded} config={config} />
      </div>
    </BottomSticky>
  );
}

export default (props) => <ComponentErrorBoundary><QuickLinksTray {...props} /></ComponentErrorBoundary>;
